import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import { Icon } from '../../../../atoms/Icon'
import type { EmploymentTrend } from '../../employmentService'

interface SectorEvolutionChartProps {
  data: EmploymentTrend[]
  height?: number
  t: TFunction
}

export interface SectorEvolutionChartRef {
  exportChart: () => string | null
}

export const SectorEvolutionChart = forwardRef<
  SectorEvolutionChartRef,
  SectorEvolutionChartProps
>(({ data, height = 400, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.year - b.year)

    // Prepare data arrays
    const years = sortedData.map((d) => d.year.toString())
    const sector1Data = sortedData.map((d) => d.employment_sector_1)
    const sector2Data = sortedData.map((d) => d.employment_sector_2)
    const sector3Data = sortedData.map((d) => d.employment_sector_3)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: function (params: any) {
          const year = params[0].axisValue
          const primaryLabel = t('charts.sectorEvolution.legend.primarySector')
          const secondaryLabel = t(
            'charts.sectorEvolution.legend.secondarySector'
          )
          const tertiaryLabel = t(
            'charts.sectorEvolution.legend.tertiarySector'
          )
          const sector1 =
            params.find((p: any) => p.seriesName === primaryLabel)?.value || 0
          const sector2 =
            params.find((p: any) => p.seriesName === secondaryLabel)?.value || 0
          const sector3 =
            params.find((p: any) => p.seriesName === tertiaryLabel)?.value || 0
          const total = sector1 + sector2 + sector3

          const sector1Pct =
            total > 0 ? ((sector1 / total) * 100).toFixed(1) : '0'
          const sector2Pct =
            total > 0 ? ((sector2 / total) * 100).toFixed(1) : '0'
          const sector3Pct =
            total > 0 ? ((sector3 / total) * 100).toFixed(1) : '0'

          return `
            <div style="text-align: left;">
              <strong>${t('charts.sectorEvolution.tooltips.year')}: ${year}</strong><br/>
              <span style="color: #f59e0b;">● ${t('sectors.primary.short')}: ${sector1.toLocaleString()} (${sector1Pct}%)</span><br/>
              <span style="color: #3b82f6;">● ${t('sectors.secondary.short')}: ${sector2.toLocaleString()} (${sector2Pct}%)</span><br/>
              <span style="color: #10b981;">● ${t('sectors.tertiary.short')}: ${sector3.toLocaleString()} (${sector3Pct}%)</span><br/>
              <span style="color: #6b7280;">● ${t('charts.sectorEvolution.tooltips.total')}: ${total.toLocaleString()}</span>
            </div>
          `
        },
      },
      legend: {
        data: [
          t('charts.sectorEvolution.legend.primarySector'),
          t('charts.sectorEvolution.legend.secondarySector'),
          t('charts.sectorEvolution.legend.tertiarySector'),
        ],
        top: 10,
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: years,
        axisLabel: {
          fontSize: 11,
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
      },
      yAxis: {
        type: 'value',
        name: t('charts.sectorEvolution.axis.employment'),
        axisLabel: {
          fontSize: 11,
          formatter: function (value: number) {
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k'
            }
            return value.toString()
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: t('charts.sectorEvolution.legend.primarySector'),
          type: 'line',
          stack: 'Total',
          areaStyle: {
            color: '#f59e0b',
          },
          lineStyle: {
            color: '#f59e0b',
            width: 2,
          },
          itemStyle: {
            color: '#f59e0b',
          },
          emphasis: {
            focus: 'series',
          },
          data: sector1Data,
        },
        {
          name: t('charts.sectorEvolution.legend.secondarySector'),
          type: 'line',
          stack: 'Total',
          areaStyle: {
            color: '#3b82f6',
          },
          lineStyle: {
            color: '#3b82f6',
            width: 2,
          },
          itemStyle: {
            color: '#3b82f6',
          },
          emphasis: {
            focus: 'series',
          },
          data: sector2Data,
        },
        {
          name: t('charts.sectorEvolution.legend.tertiarySector'),
          type: 'line',
          stack: 'Total',
          areaStyle: {
            color: '#10b981',
          },
          lineStyle: {
            color: '#10b981',
            width: 2,
          },
          itemStyle: {
            color: '#10b981',
          },
          emphasis: {
            focus: 'series',
          },
          data: sector3Data,
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height, t])

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.sectorEvolution.noData')}
      </div>
    )
  }

  const sortedData = [...data].sort((a, b) => a.year - b.year)
  const firstYear = sortedData[0]
  const lastYear = sortedData[sortedData.length - 1]

  const firstTotal =
    firstYear.employment_sector_1 +
    firstYear.employment_sector_2 +
    firstYear.employment_sector_3
  const lastTotal =
    lastYear.employment_sector_1 +
    lastYear.employment_sector_2 +
    lastYear.employment_sector_3

  const firstSector3Pct =
    firstTotal > 0 ? (firstYear.employment_sector_3 / firstTotal) * 100 : 0
  const lastSector3Pct =
    lastTotal > 0 ? (lastYear.employment_sector_3 / lastTotal) * 100 : 0
  const sectorShift = lastSector3Pct - firstSector3Pct

  return (
    <div>
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: `${height}px`,
          minHeight: '300px',
        }}
      />

      {/* Evolution insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon name="trending-up" size="xs" />{' '}
          {t('charts.sectorEvolution.summary.title', {
            startYear: firstYear.year,
            endYear: lastYear.year,
          })}
        </div>
        <div
          style={{
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.4',
          }}
        >
          •{' '}
          <strong>
            {t('charts.sectorEvolution.summary.serviceSectorTrend')}:
          </strong>{' '}
          {sectorShift >= 0 ? '+' : ''}
          {sectorShift.toFixed(1)}% ({firstSector3Pct.toFixed(1)}% →{' '}
          {lastSector3Pct.toFixed(1)}%)
          <br />•{' '}
          <strong>
            {t('charts.sectorEvolution.summary.economicTransformation')}:
          </strong>{' '}
          {Math.abs(sectorShift) > 5
            ? t('charts.sectorEvolution.summary.significantStructuralChange')
            : Math.abs(sectorShift) > 2
              ? t('charts.sectorEvolution.summary.gradualTransition')
              : t('charts.sectorEvolution.summary.stableSectorComposition')}
          <br />•{' '}
          <strong>
            {t('charts.sectorEvolution.summary.totalEmploymentChange')}:
          </strong>{' '}
          {(((lastTotal - firstTotal) / firstTotal) * 100).toFixed(1)}% (
          {firstTotal.toLocaleString()} → {lastTotal.toLocaleString()})
        </div>
      </div>
    </div>
  )
})

SectorEvolutionChart.displayName = 'SectorEvolutionChart'
